import { z } from 'zod'
import { Compartiment, Contract, type ContractFormat, ContractProperty, type ContractRegistry, type ContractRole, JsonTypeName } from './contract.js'
import type { Definitie } from './definitie.js'
import { Block, CriterionId, UitvraagRef } from './id.js'
import { Verdict } from './laag.js'
import { RESPONSE_SHAPE, COMPONENTS, type ResponseShape, type Component, type Control } from './field.js'

/**
 * Het gestructureerde deel van het contract van een eigen bouwblok, gegenereerd (D-37, D-42,
 * R-53, ontwerp 9.5).
 *
 * **Waarom dit bestaat.** Voor een blok buiten dit product levert de werkgroep het
 * gestructureerde deel, in het formaat dat dit product voorschrijft (U27). Voor een eigen blok
 * schrijven wij het zelf, en dan hoort het uit de bron te komen waar de vorm al staat en niet
 * uit handwerk ernaast: de schema-s in de registry worden gegenereerd uit de Zod-bronnen in
 * `packages/model` waar het om eigen blokken gaat (ontwerp 9.5). Dit is die helft.
 *
 * **Wat er wordt gegenereerd, en waarom precies dat.** Twee dingen, en dat is besluit 7 van M7
 * opdracht 9. De vaste componenten leveren hun gegevens gestructureerd aan het dossier en niet
 * als tekstblok (R-53), en de uitkomst per criterium hoort bij het document van het eigen blok
 * (D-42, R-152). Beide krijgen daarom een eigen plek in het gestructureerde deel, in plaats van
 * een compartiment: het blok beschrijft dan de vorm die het verwacht, in plaats van dat het
 * formulier hem verzint. De overige velden die de inrichter zelf toevoegt landen wél via hun
 * compartiment (D-69), en die komen er bij publicatie in.
 *
 * **Wat er niet wordt gegenereerd.** `$stand` staat niet in het gestructureerde deel; het is een
 * gereserveerde sleutel op de wortel van het blokdocument (D-72) en `contract.ts` weigert hem
 * juist als eigenschap. Het document dat de stand draagt wordt elders gebouwd. De compartimenten
 * gaan onveranderd mee zoals de bouwblokdefinitie ze levert: welke compartimenten een bouwblok
 * heeft en welk compartiment het doorgeeft staat daar en niet in het formulier (D-70), en de
 * velden erin komen bij publicatie van het formulier dat erin schrijft (D-69, D-71). Dat een
 * veld verwijst naar een compartiment dat werkelijk in het contract staat wordt niet hier maar
 * in de publicatiepoort getoetst (`compartimentGaps` in `packages/engine/src/publicatiecontrole.ts`);
 * twee plekken met dezelfde controle zouden twee meldingen over hetzelfde geval opleveren.
 */

/**
 * Wat de bouwblokdefinitie levert en het formulier niet (D-41, D-70, U26).
 *
 * De ronde, de naam en de beschrijving van het bouwblok, het gestructureerde deel dat het blok
 * zelf al vastlegt voordat er één formulier is gemaakt, en zijn compartimenten. Die staan vast
 * voordat de formulieren worden gemaakt (D-41), dus ze komen hier binnen en worden hier niet
 * bedacht: een generator die zelf compartimenten verzint zou D-70 omdraaien.
 */
export const BuildingBlockDefinitie = z.strictObject({
  blok: Block,
  /**
   * De ronde zoals het `$id` van een contract hem eist (`grantify:contract:<blok>:<ronde>`).
   * Dezelfde vorm als daar, want het is dezelfde ronde; een contract van een blok in een
   * gepubliceerde ronde wijzigt niet (D-43).
   */
  ronde: z.string().regex(/^[0-9]{4}-[0-9]+$/, 'verwacht een ronde als <jaar>-<nummer>, zoals 2026-1'),
  /** De naam van het bouwblok zoals die in de bouwbloklijst van de configurator komt te staan. */
  title: z.string().min(1),
  /** Wat het blok op het dossier achterlaat. */
  description: z.string().min(1),
  /**
   * Het gestructureerde deel dat het bouwblok zelf al draagt, naast wat deze functie genereert.
   * Optioneel, en bij de eigen blokken van vandaag leeg: hun gestructureerde deel is de vaste
   * componenten en de uitkomst per criterium, en die schrijft de generator zelf (D-42). Een veld
   * van de inrichter hoort hier niet, dat landt in zijn compartiment (D-69).
   */
  properties: z.record(z.string().min(1), ContractProperty).optional(),
  compartimenten: z.array(Compartiment).optional(),
})
export type BuildingBlockDefinitie = z.infer<typeof BuildingBlockDefinitie>

/** Eén eigenschap zoals de generator hem opschrijft: de Zod-bron voor het type, plus betekenis en rol. */
interface PropertyHeader {
  /** De Zod-bron waaruit het type wordt afgeleid; nergens een tweede definitie van hetzelfde begrip. */
  readonly bron: z.core.$ZodType
  /** De betekenis die D-37 eist: zonder betekenis is een pad niet te kiezen (D-38, R-59). */
  readonly description: string
  /** De rol in de groepering van de reeks (D-73); afwezig betekent: doet niet mee. */
  readonly rol?: ContractRole
  /** Bij een eigenschap die een object is: de eigenschappen erin, zodat het contract ze ook beschrijft. */
  readonly properties?: Readonly<Record<string, PropertyHeader>>
  /** Niet elk item heeft hem; zonder deze vlag is een eigenschap van een item verplicht. */
  readonly optioneel?: true
  /**
   * Bij een tekst die een datum of tijdstip is. Met de hand en niet uit de Zod-bron: `Moment` is een
   * tekst met een patroon, en dat zegt `z.toJSONSchema` niet als formaat.
   */
  readonly format?: ContractFormat
}

/** Een tekst in beide talen, als eigenschap: een object met `nl` en `en` (R-144). */
function inBothLanguages(description: string, optional?: true): PropertyHeader {
  return {
    bron: z.object({ nl: z.string(), en: z.string() }),
    description,
    properties: {
      nl: { bron: z.string(), description: 'De tekst in het Nederlands.' },
      en: { bron: z.string(), description: 'De tekst in het Engels.' },
    },
    ...(optional === undefined ? {} : { optioneel: optional }),
  }
}

/**
 * De naam van de reeksdragende eigenschap met de uitkomst per criterium.
 *
 * Eén document is één stand, dus deze eigenschap draagt alleen de criteria van díe stand; de
 * reeks over eerdere instanties ontstaat doordat het formulier de items uit alle genoemde
 * standen aaneenrijgt, oudste stand eerst (R-62, R-201, besluit 6 van M7 opdracht 9).
 */
export const CRITERIA = 'criteria'

/**
 * De eigenschappen die uit `Oordeel` in `laag.ts` komen (ontwerp 9.5, R-152).
 *
 * Het `satisfies` is de vangrail: komt er een veld bij `Oordeel`, dan valt de typecheck hier om
 * en moet er een keuze worden gemaakt, in plaats van dat het veld stil buiten het koppelvlak
 * blijft. Dezelfde vorm als `RESPONSE_SHAPE` in `veld.ts`.
 */
const CRITERION_FROM_VERDICT = {
  oordeel: {
    bron: Verdict.shape.oordeel,
    rol: 'antwoord',
    description:
      'De schaalwaarde die de beoordelaar heeft gekozen: een getal bij een cijferbereik, tekst bij een tweekeuze (R-148).',
  },
  voldoet: {
    bron: Verdict.shape.voldoet,
    rol: 'antwoord',
    description:
      'Of het criterium met dit oordeel is afgedaan. Geleverd door de beoordelaar en nooit afgeleid uit de waarde van het oordeel: welke waarden als niet-voldoet gelden is een ronde-instelling (R-148, R-151).',
  },
  onderbouwing: {
    bron: Verdict.shape.onderbouwing,
    rol: 'antwoord',
    description: 'Waarom de beoordelaar tot dit oordeel komt; altijd verplicht (R-147).',
  },
  toetser: {
    bron: Verdict.shape.toetser,
    description:
      'Wie het oordeel heeft gegeven, apart van het oordeel zelf (R-152, R-187), zodat een lezer het geanonimiseerd kan krijgen (R-114).',
  },
  revisieId: {
    bron: Verdict.shape.revisieId,
    description: 'De $stand van het document waarop dit oordeel rust (R-175, D-72).',
  },
  // Sinds 23 oktober 2026 in het contract: het bericht droeg het moment al, en het contract
  // beschrijft precies wat er over de lijn gaat (`koppelvlak.md` hoofdstuk 2).
  moment: {
    bron: Verdict.shape.moment,
    format: 'date-time',
    description: 'Wanneer het oordeel is gegeven.',
  },
} as const satisfies Record<keyof z.infer<typeof Verdict>, PropertyHeader>

/**
 * De eigenschappen van één criteriumuitkomst, in de orde waarin ze in het document staan.
 *
 * De periode en het criterium komen niet uit `Oordeel`: de periode is de uitvraagaanduiding van
 * de instantie die de stand uitgaf (`UitvraagRef`, D-73) en het criterium is de stabiele
 * criterium-identiteit die ook buiten dit product bruikbaar is (`CriteriumId`, R-150).
 */
const CRITERION_PROPERTIES = {
  periode: {
    bron: UitvraagRef,
    rol: 'periode',
    description:
      'Het uitvraagmoment van de instantie die deze stand uitgaf; waarmee de lezer eerdere rondes van elkaar kan onderscheiden (R-62).',
  },
  criterium: {
    bron: CriterionId,
    rol: 'vraag',
    description: 'De stabiele criterium-identiteit, ook buiten het formulier bruikbaar (R-150).',
  },
  label: inBothLanguages('De vraag van het criterium, zoals de beoordelaar haar zag (R-144).'),
  ...CRITERION_FROM_VERDICT,
  oordeelTekst: inBothLanguages(
    'De tekst van het gekozen oordeel op de schaal, zoals de beoordelaar hem zag; ontbreekt bij een schaal zonder teksten (R-148).',
    true,
  ),
} as const satisfies Record<string, PropertyHeader>

/**
 * De rol per eigenschap van de criteria-reeks (D-73), voor wie moet groeperen zonder een contract
 * op te vragen dat de pod niet heeft (`packages/renderer/src/veld/controls/Subgrid.tsx`, opdracht
 * B3). Afgeleid van `CRITERION_PROPERTIES` en dus geen tweede definitie: een eigenschap zonder
 * rol (`toetser`, `revisieId`) doet niet mee.
 */
export const CRITERION_ROLES: Readonly<Record<string, ContractRole>> = Object.fromEntries(
  (Object.entries(CRITERION_PROPERTIES) as [string, PropertyHeader][])
    .filter((pair) => pair[1].rol !== undefined)
    .map(([name, header]) => [name, header.rol as ContractRole]),
)

/**
 * Wat elk vast component op het dossier achterlaat (R-53, R-46).
 *
 * De plek is per componentsoort en niet per geplaatst veld: een component is vast gebouwd en
 * brengt zijn eigen structuur mee (R-46), dus wat het blok oplevert hangt aan de soort en niet
 * aan het id dat de inrichter eraan heeft gegeven. Wat er binnen die plek staat volgt met het
 * component zelf: van de zeven vaste componenten is vandaag alleen de buitenkant gebouwd, en de
 * binnenkant wacht per component op iets anders (R-47 tot R-52, U10). Een vorm verzinnen die het
 * component daarna niet levert zou een koppelend systeem op een belofte laten binden.
 *
 * Het `satisfies` is dezelfde vangrail als hierboven: komt er een componentsoort bij, dan valt de
 * typecheck om tot iemand zegt wat het blok ervan op het dossier achterlaat.
 */
const COMPONENT_DESCRIPTION = {
  profiel: 'De profielgegevens van de aanvrager, gestructureerd op het dossier (R-47, R-53).',
  projectbetrokkenen: 'De projectbetrokkenen met hun rol, gestructureerd op het dossier (R-48, R-53).',
  publicatielijst: 'De opgevoerde publicaties, gestructureerd op het dossier (R-49, R-53).',
  referentielijst:
    'De bronvermeldingen uit de rich text met de daaruit berekende literatuurlijst (R-50, R-53).',
  outputRegistreren: 'De geregistreerde output van het project (R-51, R-53).',
  sjabloon: 'Het ingevulde sjabloon dat bij dit formulier is klaargezet (R-52, R-53).',
  begroting: 'De begroting per post; de vorm binnen deze plek volgt met het component (U10, R-53).',
  cofinanciering:
    'De cofinanciering per partij; de vorm binnen deze plek volgt met het component (U10, R-53).',
  lening: 'De leningsgegevens; de vorm binnen deze plek volgt met het component (U10, R-53).',
} as const satisfies Record<Component, string>

/**
 * Het JSON-type van een eigenschap, afgeleid uit zijn Zod-bron.
 *
 * Langs `z.toJSONSchema`, dezelfde omzetting die `jsonschema.ts` gebruikt, zodat er geen tweede
 * lezing van een Zod-type ontstaat. Een union van eenvoudige typen wordt een typelijst: zo valt
 * `["integer", "string"]` uit de union in `Oordeel.oordeel` in plaats van dat iemand hem
 * overtypt (R-148, besluit 3 van M7 opdracht 9).
 *
 * Lukt het niet, dan is dat een fout en geen gok: een verkeerd getypeerd contract valt pas op bij
 * de eerste levering, en dan is de ronde al ingericht.
 */
function jsonTypeOf(source: z.core.$ZodType, where: string): JsonTypeName | JsonTypeName[] {
  const shape = z.toJSONSchema(source, { io: 'input' }) as Record<string, unknown>
  const single = JsonTypeName.safeParse(shape['type'])
  if (single.success) return single.data
  const branches = shape['anyOf']
  if (Array.isArray(branches)) {
    const types: JsonTypeName[] = []
    for (const branch of branches) {
      const out = JsonTypeName.safeParse((branch as Record<string, unknown>)['type'])
      if (!out.success) break
      types.push(out.data)
    }
    if (types.length === branches.length && types.length > 0) return types
  }
  throw new Error(`het type van ${where} is niet uit zijn Zod-bron af te leiden: ${JSON.stringify(shape)}`)
}

/** Eén eigenschap opgeschreven zoals het contract hem draagt. */
function property(name: string, header: PropertyHeader): Record<string, unknown> {
  const nested = header.properties
  const format = header.format
  return {
    type: jsonTypeOf(header.bron, name),
    ...(format === undefined ? {} : { format }),
    ...(header.rol === undefined ? {} : { rol: header.rol }),
    description: header.description,
    ...(nested === undefined
      ? {}
      : {
          required: Object.entries(nested)
            .filter(([, inner]) => inner.optioneel === undefined)
            .map(([key]) => key),
          properties: Object.fromEntries(Object.entries(nested).map(([key, inner]) => [key, property(`${name}.${key}`, inner)])),
        }),
  }
}

/**
 * De componentsoorten die in deze definitie zijn geplaatst, in de orde waarin ze voorkomen.
 *
 * Geëxporteerd sinds `blokdocumentbouwer.ts` (B4a) dezelfde vraag heeft: welke componentplekken
 * hoort dit blokdocument te dragen. Twee lezers en één antwoord, dezelfde reden als
 * `needsCompartiment` in `definitie.ts`.
 */
export function componentsOf(definitie: Definitie): readonly Component[] {
  const seen = new Set<Component>()
  for (const section of definitie.secties) {
    for (const field of section.velden) {
      // Een component of criterium in een rij van een herhaalgroep bestaat niet (`veld.ts`),
      // dus de bovenste laag velden is de hele verzameling.
      if (field.control === 'component') seen.add(field.component)
    }
  }
  return COMPONENTS.filter((kind) => seen.has(kind))
}

/**
 * Draagt deze definitie criteria, en levert het blok dus een uitkomst per criterium op (D-42)?
 *
 * Geëxporteerd om dezelfde reden als `componentsOf`: `blokdocumentbouwer.ts` (B4a) moet weten
 * of de sleutel `criteria` in het document hoort, zonder dat opnieuw op te schrijven.
 */
export function hasCriteria(definitie: Definitie): boolean {
  return definitie.secties.some((section) => section.velden.some((field) => field.control === 'criterium'))
}

/** De reeksdragende eigenschap met de uitkomst per criterium (D-42, D-73, R-152). */
export function criteriaProperty(): Record<string, unknown> {
  const properties: Record<string, unknown> = {}
  for (const [name, header] of Object.entries(CRITERION_PROPERTIES) as [string, PropertyHeader][]) {
    properties[name] = property(name, header)
  }
  return {
    type: 'array',
    description:
      'De uitkomst per criterium in deze stand: oordeel, onderbouwing en beoordelaar apart (R-152, R-187). De reeks over eerdere instanties ontstaat doordat de items van alle genoemde standen aaneen worden geregen, oudste stand eerst (R-62, R-201).',
    items: {
      type: 'object',
      description:
        'Eén criteriumuitkomst: het criterium met zijn oordeel en onderbouwing, en de periode waarin het is gegeven (R-62, D-42).',
      // Verplicht is wat elk item heeft, en dat is afgeleid en niet gekozen: `Oordeel` kent geen
      // optioneel veld, en een uitkomst zonder criterium of zonder periode is in de aaneengeregen
      // reeks niet te plaatsen. Alleen de tekst van het oordeel ontbreekt bij een schaal zonder teksten.
      required: (Object.entries(CRITERION_PROPERTIES) as [string, PropertyHeader][])
        .filter(([, header]) => header.optioneel === undefined)
        .map(([name]) => name),
      properties,
    },
  }
}

/**
 * Schrijft het contract van een eigen bouwblok (D-37, D-42, R-53).
 *
 * De formulierdefinitie levert wat het formulier oplevert, de bouwblokdefinitie levert wat van
 * het bouwblok is (D-70). Weigeren in plaats van repareren: een formulier van een ander blok kan
 * dit contract niet beschrijven, en een gegenereerde plek die al bezet is wordt niet stil
 * overschreven.
 */
export function generateContract(definitie: Definitie, buildingBlock: BuildingBlockDefinitie): Contract {
  const block = BuildingBlockDefinitie.parse(buildingBlock)
  if (block.blok !== definitie.blok) {
    throw new Error(
      `definitie ${definitie.id} hoort bij blok ${definitie.blok} en kan het contract van ${block.blok} niet beschrijven`,
    )
  }

  const properties: Record<string, unknown> = { ...block.properties }
  const generated: Record<string, unknown> = {}
  for (const kind of componentsOf(definitie)) {
    generated[kind] = { type: 'object', description: COMPONENT_DESCRIPTION[kind] }
  }
  if (hasCriteria(definitie)) generated[CRITERIA] = criteriaProperty()

  for (const [name, shape] of Object.entries(generated)) {
    if (Object.hasOwn(properties, name)) {
      throw new Error(
        `blok ${block.blok} draagt zelf al een eigenschap ${name}, dus de gegenereerde plek zou hem overschrijven`,
      )
    }
    properties[name] = shape
  }

  // Door het eigen schema voordat het contract de deur uit gaat: wat de generator oplevert
  // wordt aan dezelfde regels gehouden als een aangeleverd contract, inclusief het verbod op de
  // gereserveerde sleutels (D-37, D-69, D-72).
  return Contract.parse({
    $schema: 'https://json-schema.org/draft/2020-12/schema',
    $id: `grantify:contract:${block.blok}:${block.ronde}`,
    title: block.title,
    description: block.description,
    type: 'object',
    properties,
    ...(block.compartimenten === undefined ? {} : { compartimenten: block.compartimenten }),
  })
}

/**
 * Het JSON-type van een antwoordvorm, coarse en niet per controltype (D-37, ontwerp 9.5).
 *
 * **Waarom coarse genoeg is.** `contract.ts` zegt het zelf: het schema toetst of een pad genoeg
 * beschrijft om te kiezen en bij publicatie te controleren, niet meer. `field.ts`'s `Source` (waar
 * een verwijzend veld zijn bron aanwijst) draagt `{contract, pad}`, zonder een verwacht type; de
 * poort schrijft er bij een verwijzing alleen het formaat bij voor de weergave (R-54). Er is geen typecontrole tussen een verwijzend veld en zijn bron om aan te sluiten, dus
 * een fijner type hier zou niets afdwingen en alleen de tabel groter maken. `waarden`,
 * `documenten` en `rijen` worden allemaal `array`: het onderscheid tussen een lijst trefwoorden,
 * een lijst bestanden en een lijst rijen zit al in de betekenis (`description`), niet in het
 * JSON-type.
 *
 * `laag` en `geen` komen hier nooit aan: `hasCompartimentNeeded` in `definitie.ts` sluit ze al
 * uit voordat een veld een `compartiment` mag dragen. Ze staan er alleen omdat dit een `Record`
 * over heel `ResponseShape` is en geen tweede, onvolledige lijst.
 */
const COMPARTIMENT_TYPE_COARSE: Record<ResponseShape, JsonTypeName> = {
  waarde: 'string',
  waarden: 'array',
  documenten: 'array',
  rijen: 'array',
  laag: 'string',
  geen: 'string',
}

/** De drie controls waarvan de waarde geen tekst is; alle andere `waarde`-controls worden `string`. */
const COMPARTIMENT_TYPE_FINE: Partial<Record<Control, JsonTypeName>> = {
  number: 'number',
  currency: 'number',
  rating: 'number',
  slider: 'number',
  toggle: 'boolean',
  akkoordPerPersoon: 'boolean',
}

function compartimentType(control: Control): JsonTypeName {
  return COMPARTIMENT_TYPE_FINE[control] ?? COMPARTIMENT_TYPE_COARSE[RESPONSE_SHAPE[control]]
}

/**
 * Vult de compartiment-shells van een bouwblokdefinitie met de velden die er werkelijk in landen,
 * uit de gepubliceerde formulierdefinitie van datzelfde blok (D-69, D-70, D-71, ontwerp 9.5, M7
 * opdracht B6).
 *
 * **Een sibling van `genereerContract`, en nooit een wijziging erin.** `genereerContract` laat
 * `compartimenten` bewust onveranderd: welke compartimenten een blok heeft en welk compartiment
 * het doorgeeft is van het bouwblok, niet van het formulier (D-70,
 * `contractgenerator.test.ts` "de compartimenten gaan onveranderd mee"). Deze functie voegt
 * daar de andere helft van D-69 aan toe, apart, want ze speelt pas zodra het formulier van dát
 * blok is gepubliceerd: tot dan levert de bouwblokdefinitie lege compartimenten (D-71,
 * `voorbeelden.test.ts` "de compartimenten van een aangeleverde registry zijn nog leeg"), en
 * die stand blijft ongewijzigd waar deze functie niets aantreft om te vullen.
 *
 * **Alleen de bovenste laag velden, en dat is geen keuze maar een afgeleide.** Een veld in een
 * rij van een herhaalgroep draagt zelf geen `compartiment`: het landt in het compartiment van
 * zijn herhaalgroep en niet apart (`definitie.ts`, `needsCompartiment`). Deze functie hoeft
 * dat dus niet zelf te bewaken en loopt daarom niet door `rijVelden`.
 *
 * **De volgorde van de aanroep doet ertoe: `apps/configurator/src/publiceren.ts` roept dit alleen
 * aan voor een blok waarvan de store een publicatie kent** (`GET /v1/publicaties?blok=`,
 * `docs/dekking.md` M7 besluit 5 van opdracht 9). Deze functie zelf toetst dat niet: ze weet
 * alleen van een `Definitie`, niet van publicatiestatus, en dat hoort ook zo (M0.7: het model
 * kent geen opslag).
 */
export function fillCompartimentFields(
  compartimenten: readonly Compartiment[],
  definitie: Definitie,
): readonly Compartiment[] {
  const perCompartiment: Record<string, Record<string, unknown>> = {}
  for (const section of definitie.secties) {
    for (const field of section.velden) {
      if (!('compartiment' in field) || field.compartiment === undefined) continue
      ;(perCompartiment[field.compartiment] ??= {})[field.id] = {
        type: compartimentType(field.control),
        // Een bedragveld is een bedrag in euro, ook voor een lezer van buiten die de definitie niet
        // heeft (R-54, `koppelvlak.md` hoofdstuk 2).
        ...(field.control === 'currency' ? { format: 'amount', valuta: 'EUR' } : {}),
        description: field.label.nl,
      }
    }
  }
  return compartimenten.map((compartiment) => ({
    ...compartiment,
    velden: {
      ...compartiment.velden,
      properties: { ...compartiment.velden.properties, ...perCompartiment[compartiment.id] },
    },
  }))
}

/**
 * Het contract van een eigen blok zoals het bij één publicatie geldt (`koppelvlak.md` hoofdstuk 2):
 * het gestructureerde deel uit deze definitie, en de compartimenten uit de registry met de velden die
 * deze definitie erin schrijft (D-69). Dit is wat bij het publiceren als XML Schema wordt bevroren.
 *
 * Uit de registry zoals zij is aangeleverd, en niet uit een verrijkte: die draagt de velden van een
 * eerdere publicatie van hetzelfde blok, en die horen niet bij deze. Geeft niets terug voor een blok
 * dat de registry niet kent.
 */
export function publicationContract(definitie: Definitie, registry: ContractRegistry): Contract | undefined {
  const row = registry.bouwblokken.find((candidate) => candidate.blok === definitie.blok)
  if (row === undefined) return undefined
  const ronde = row.contract.$id.split(':')[3] ?? ''
  return generateContract(definitie, {
    blok: row.blok,
    ronde,
    title: row.contract.title,
    description: row.contract.description,
    ...(row.contract.compartimenten === undefined
      ? {}
      : { compartimenten: [...fillCompartimentFields(row.contract.compartimenten, definitie)] }),
  })
}
